import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Text, VStack, HStack, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber,
  Spinner, Button, useToast, useDisclosure, Drawer, DrawerOverlay,
  DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Badge, Icon, Input, InputGroup, InputLeftElement, NumberInput, NumberInputField,
  NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Flex
} from '@chakra-ui/react';
import { FiUsers, FiChevronRight, FiSearch, FiFilter, FiRefreshCw } from 'react-icons/fi';
import ClientDashboard from './ClientDashboard';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';

export default function CustomersTab() {
  const [allClientsData, setAllClientsData] = useState([]); // Full dataset
  const [filteredClientsData, setFilteredClientsData] = useState([]); // Filtered dataset
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed at 10 rows per page
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [minIssueCount, setMinIssueCount] = useState('');
  const [maxIssueCount, setMaxIssueCount] = useState('');
  
  const toast = useToast();
  const { makeRequest } = useAnalyticsAPI();

  // Fetch all clients data (no pagination from API - we'll handle pagination client-side)
  const fetchAllClientsData = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching all clients data...');
      const result = await makeRequest('/api/bugs/analytics/individual-metrics', {
        query: {
          includeDetails: true,
          includeSummary: true
        }
      });
      
      console.log('Clients API Response:', result);
      
      if (result.status === 'success' && result.data?.metrics?.affectedClients) {
        const clientsData = result.data.metrics.affectedClients;
        console.log('All clients data loaded:', clientsData.length, 'clients');
        
        setAllClientsData(clientsData);
        
        toast({
          title: "Clients Data Loaded",
          description: `Loaded ${clientsData.length} clients`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.log('No clients data in response');
        setAllClientsData([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch clients data:', error);
      setAllClientsData([]);
      toast({
        title: "Error",
        description: `Failed to load clients data: ${error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to the full dataset
  const applyFilters = useMemo(() => {
    let filtered = [...allClientsData];
    
    console.log('Applying customer filters:', { searchTerm, minIssueCount, maxIssueCount });
    console.log('Initial customers count:', filtered.length);
    
    // Search filter (case-insensitive) - search in client name
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(client => {
        const clientName = client.name?.toLowerCase() || '';
        const matches = clientName.includes(searchLower);
        console.log(`Client "${client.name}" matches search "${searchTerm}": ${matches}`);
        return matches;
      });
      console.log('After search filter:', filtered.length, 'customers');
    }
    
    // Issue count range filter
    if (minIssueCount !== '' && minIssueCount !== null) {
      const minCount = parseInt(minIssueCount);
      if (!isNaN(minCount)) {
        filtered = filtered.filter(client => {
          const matches = client.count >= minCount;
          console.log(`Client "${client.name}" (count: ${client.count}) >= ${minCount}: ${matches}`);
          return matches;
        });
        console.log('After min count filter:', filtered.length, 'customers');
      }
    }
    
    if (maxIssueCount !== '' && maxIssueCount !== null) {
      const maxCount = parseInt(maxIssueCount);
      if (!isNaN(maxCount)) {
        filtered = filtered.filter(client => {
          const matches = client.count <= maxCount;
          console.log(`Client "${client.name}" (count: ${client.count}) <= ${maxCount}: ${matches}`);
          return matches;
        });
        console.log('After max count filter:', filtered.length, 'customers');
      }
    }
    
    console.log('Final filtered customers count:', filtered.length, 'customers');
    return filtered;
  }, [allClientsData, searchTerm, minIssueCount, maxIssueCount]);

  // Update filtered data when filters change
  useEffect(() => {
    setFilteredClientsData(applyFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, [applyFilters]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredClientsData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredClientsData.slice(startIndex, endIndex);
  
  const hasNext = currentPage < totalPages;
  const hasPrev = currentPage > 1;

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrev) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setMinIssueCount('');
    setMaxIssueCount('');
  };

  useEffect(() => {
    fetchAllClientsData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClientClick = (client) => {
    setSelectedClient(client);
    onOpen();
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  if (loading && allClientsData.length === 0) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading customers data...</Text>
      </VStack>
    );
  }

  if (allClientsData.length === 0) {
    return (
      <VStack spacing={6} align="stretch">
        <Card>
          <CardBody>
            <VStack spacing={4} py={12} textAlign="center">
              <Icon as={FiUsers} size="3rem" color="gray.400" />
              <Text fontSize="xl" fontWeight="bold" color="gray.600">
                No Customers Data Available
              </Text>
              <Text color="gray.500" maxW="md">
                No customers data found. This could mean:
                <br />• Tickets are still being processed by the AI system
                <br />• No tickets have been submitted for analysis
                <br />• The API is not returning customer analytics data
              </Text>
              <HStack spacing={4}>
                <Button
                  leftIcon={<Icon as={FiUsers} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={fetchAllClientsData}
                  isLoading={loading}
                >
                  Retry Loading
                </Button>
                <Button
                  leftIcon={<Icon as={FiRefreshCw} />}
                  colorScheme="orange"
                  variant="outline"
                  onClick={() => {
                    toast({
                      title: "Processing Status",
                      description: "Check the 'Process All Tickets' button on the Overview tab to see processing status",
                      status: "info",
                      duration: 5000,
                      isClosable: true,
                    });
                  }}
                >
                  Check Processing Status
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header Stats */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Customers</StatLabel>
              <StatNumber color="blue.500">{allClientsData.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Filtered Results</StatLabel>
              <StatNumber color="green.500">{filteredClientsData.length}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Issues</StatLabel>
              <StatNumber color="red.500">
                {allClientsData.reduce((sum, client) => sum + client.count, 0)}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filter Options */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack>
              <Icon as={FiFilter} color="blue.500" />
              <Text fontSize="lg" fontWeight="bold">Filter Options</Text>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              {/* Search Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Search Customer Name</Text>
                <InputGroup>
                  <InputLeftElement>
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Box>
              
              {/* Min Issue Count Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Min Issue Count</Text>
                <NumberInput
                  min={0}
                  value={minIssueCount}
                  onChange={(value) => setMinIssueCount(value)}
                >
                  <NumberInputField placeholder="Min issues" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
              
              {/* Max Issue Count Filter */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Max Issue Count</Text>
                <NumberInput
                  min={0}
                  value={maxIssueCount}
                  onChange={(value) => setMaxIssueCount(value)}
                >
                  <NumberInputField placeholder="Max issues" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
            </SimpleGrid>
            
            {/* Clear Filters Button */}
            {(searchTerm || minIssueCount || maxIssueCount) && (
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={clearFilters}
                size="sm"
                alignSelf="flex-start"
              >
                Clear All Filters
              </Button>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <HStack>
                <Icon as={FiUsers} color="blue.500" />
                <Text fontSize="lg" fontWeight="bold">Customers</Text>
              </HStack>
              <Text fontSize="sm" color="gray.500">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredClientsData.length)} of {filteredClientsData.length} customers
              </Text>
            </HStack>
            
            {/* Table */}
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Customer Name</Th>
                    <Th isNumeric>Issue Count</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPageData.map((client) => (
                    <Tr 
                      key={client.name} 
                      _hover={{ bg: "gray.50", cursor: "pointer" }}
                      onClick={() => handleClientClick(client)}
                    >
                      <Td fontWeight="medium">{client.name}</Td>
                      <Td isNumeric>
                        <Badge colorScheme={getSeverityColor(client.count > 10 ? 'high' : client.count > 5 ? 'medium' : 'low')}>
                          {client.count}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          rightIcon={<Icon as={FiChevronRight} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClientClick(client);
                          }}
                        >
                          View Details
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Flex justify="center" align="center" spacing={4}>
                <Button
                  size="sm"
                  onClick={handlePrevPage}
                  isDisabled={!hasPrev}
                  variant="outline"
                >
                  Previous
                </Button>
                
                <HStack spacing={2} mx={4}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      variant={currentPage === page ? "solid" : "outline"}
                      colorScheme={currentPage === page ? "blue" : "gray"}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  ))}
                </HStack>
                
                <Button
                  size="sm"
                  onClick={handleNextPage}
                  isDisabled={!hasNext}
                  variant="outline"
                >
                  Next
                </Button>
              </Flex>
            )}
            
            {/* No Results Message */}
            {filteredClientsData.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color="gray.500">No customers match the current filters.</Text>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  onClick={clearFilters}
                  mt={2}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Client Dashboard Drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <HStack>
              <Icon as={FiUsers} color="blue.500" />
              <Text>{selectedClient?.name} - Client Dashboard</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            {selectedClient && (
              <ClientDashboard client={selectedClient} onClose={onClose} />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
}